import type { Attribution } from '../api/contract';

const UTM: [string, keyof Attribution][] = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
];

const COOKIES: [string, keyof Attribution][] = [
  ['affwp_ref', 'affwpRef'],
  ['affwp_ref_visit_id', 'affwpVisitId'],
  ['affwp_campaign', 'affwpCampaign'],
];

export function readCookie(name: string, jar: string): string | undefined {
  const hit = jar.split(/;\s*/).find((c) => c.startsWith(`${name}=`));
  if (!hit) return undefined;
  try {
    return decodeURIComponent(hit.slice(name.length + 1));
  } catch {
    return undefined;
  }
}

/** AffiliateWP's referral variable (architecture.md §5, GF-3): `?buddy=<affiliate id or slug>`. */
export const REFERRAL_VAR = 'buddy';
const REFERRAL = /^[A-Za-z0-9_.-]{1,64}$/;

/**
 * utm_* and `?buddy=` from the landing URL, and AffiliateWP's cookies, in the contract's
 * `Attribution` shape. Empty when there is nothing to record. The trip draft keeps it for this
 * tab until `createEnquiry` (or the scan) stores it on the enquiry, which forwards it to
 * AffiliateWP as cookies at submission.
 *
 * An `affwp_ref` cookie, when there is one, wins over `?buddy=` here: that is AffiliateWP's own
 * default (the first referrer keeps the credit while its cookie lives). `applyReferral`
 * (referral.ts) then asks the API about `?buddy=`, which applies AffiliateWP's actual setting,
 * and sets AffiliateWP's cookies for its own lifetime (decision 36, answered 2026-09-29: a
 * first-party cookie; the cookie policy needs a line before go-live). The static site sets the
 * same cookies on its pages (decision 56).
 */
export function readAttribution(
  search: string,
  pathname: string,
  jar = globalThis.document?.cookie ?? '',
): Attribution {
  const qs = new URLSearchParams(search);
  const out: Attribution = {};
  for (const [param, key] of UTM) {
    const v = qs.get(param);
    if (v) out[key] = v.slice(0, 128);
  }
  const buddy = qs.get(REFERRAL_VAR)?.trim();
  if (buddy && REFERRAL.test(buddy)) out.affwpRef = buddy;
  for (const [name, key] of COOKIES) {
    const v = readCookie(name, jar);
    if (v) out[key] = v.slice(0, key === 'affwpCampaign' ? 128 : 64);
  }
  if (Object.keys(out).length) out.landingPath = pathname.slice(0, 512);
  return out;
}
